import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { App } from '@capacitor/app';

import { I18nContext } from '../context/translations/TranslationsContext.context';
import { Button } from '../components/ui/button';
import { clearContextAndLocalStorage } from '../utils/clearLocalStorageAndContext';
import { borrarAcceso } from './accesoGuardado';
import { verificarIdentidad } from './biometria';
import { haySesionAbierta } from './sesion';
import { biometriaActivada, rechazarBiometria } from './preferenciaBiometria';
import { esApp } from './plataforma';
import { textosParaDesbloquear } from './textosBiometria';
import { comprobarVersion } from './versionMinima';

/** Lo que puede estar fuera de la app sin que se le vuelva a pedir la cara al volver. */
export const MINUTOS_FUERA_PARA_VOLVER_A_PEDIR = 5;

/** Se pide solo dentro de la app, a quien lo activó y con una sesión que proteger. */
const hayQuePedir = (): boolean => esApp() && biometriaActivada() && haySesionAbierta();

/**
 * Tapa la app con una pantalla de Arca hasta que el dueño del móvil confirma con Face ID, Touch ID
 * o huella. Se pide al abrir y al volver tras {@link MINUTOS_FUERA_PARA_VOLVER_A_PEDIR} minutos fuera.
 *
 * Nunca deja al usuario encerrado: si cancela puede reintentar, si el sistema lo bloquea puede usar
 * el código del móvil, y siempre puede «Entrar con contraseña», que cierra la sesión.
 *
 * Mientras está bloqueada, la app (`#root`) queda `inert`: ni teclado ni lector de pantalla llegan
 * a ella. La pantalla se pinta fuera de `#root` y se marca como diálogo abierto, que es lo que mira
 * el botón atrás de Android (`botonAtras.ts`) para no navegar por detrás.
 *
 * En la web no pinta nada ni escucha nada.
 */
const BloqueoBiometrico: React.FC = () => {
	const { translate } = useContext(I18nContext).state;
	// Por referencia: cambiar el idioma no puede volver a lanzar los efectos (y volver a pedir la cara).
	const traducir = useRef(translate);
	traducir.current = translate;
	const [bloqueada, setBloqueada] = useState(hayQuePedir);
	const [comprobando, setComprobando] = useState(false);
	const salioEn = useRef<number | null>(null);

	const desbloquear = useCallback(async () => {
		setComprobando(true);
		let resultado: Awaited<ReturnType<typeof verificarIdentidad>> | undefined;
		try {
			// Antes de la cara, la versión (la misma comprobación que `ActualizacionObligatoria`): si
			// hay que actualizar, esa pantalla tapa la app y Face ID no debe asomar encima.
			if ((await comprobarVersion()) !== true) {
				resultado = await verificarIdentidad(textosParaDesbloquear(traducir.current));
			}
		} catch {
			// Sin versión o sin plugin: sigue bloqueada, con los botones para reintentar o entrar con
			// la contraseña.
		} finally {
			// Pase lo que pase, nunca se queda en el logo sin botones (review de #574).
			setComprobando(false);
		}

		if (resultado === 'no-disponible') {
			rechazarBiometria();
			void borrarAcceso();
		}
		if (resultado === 'ok' || resultado === 'no-disponible') {
			setBloqueada(false);
		}
	}, []);

	useEffect(() => {
		const app = document.getElementById('root');
		if (!bloqueada || !app) {
			return undefined;
		}

		app.setAttribute('inert', '');
		return () => app.removeAttribute('inert');
	}, [bloqueada]);

	// Al abrir: si hay que pedirla, se pide ya, sin esperar a que pulse nada.
	useEffect(() => {
		if (hayQuePedir()) {
			void desbloquear();
		}
	}, [desbloquear]);

	useEffect(() => {
		if (!esApp()) {
			return undefined;
		}

		const escucha = App.addListener('appStateChange', ({ isActive }) => {
			if (!isActive) {
				salioEn.current = Date.now();
				return;
			}

			const fuera = salioEn.current === null ? 0 : Date.now() - salioEn.current;
			salioEn.current = null;

			if (fuera >= MINUTOS_FUERA_PARA_VOLVER_A_PEDIR * 60 * 1000 && hayQuePedir()) {
				setBloqueada(true);
				void desbloquear();
			}
		});

		return () => {
			void escucha.then((handle) => handle.remove());
		};
	}, [desbloquear]);

	const entrarConContrasena = () => {
		// Quiere la contraseña: cerrar sesión ya hace que esa vez el login no pida la cara. Face ID
		// sigue activado.
		clearContextAndLocalStorage();
		window.location.assign('/');
	};

	if (!bloqueada) {
		return null;
	}

	return createPortal(
		<div
			role="dialog"
			data-state="open"
			aria-modal="true"
			aria-labelledby="bloqueo-biometrico-titulo"
			// `inset-y-0` y no `inset-0`: deja la pantalla fuera de las reglas de alto y centrado de los
			// diálogos de `index.css`, que la recortaban y dejaban asomar la app por debajo.
			className="fixed inset-x-0 inset-y-0 z-[2147483647] flex flex-col items-center justify-center gap-6 bg-background px-6 pb-[env(safe-area-inset-bottom)] pt-[env(safe-area-inset-top)] text-center"
		>
			<img src="/imgs/LOGO_VERDE_2.svg" alt="" className="h-12" />
			<div className="flex flex-col gap-2">
				<h1 id="bloqueo-biometrico-titulo" className="font-heading text-xl font-semibold">
					{translate('movil.biometria.bloqueo.titulo')}
				</h1>
				<p className="text-muted-foreground">{translate('movil.biometria.bloqueo.texto')}</p>
			</div>
			{!comprobando && (
				<div className="flex w-full max-w-xs flex-col gap-3">
					<Button shape="pill" onClick={() => void desbloquear()}>
						{translate('movil.biometria.bloqueo.desbloquear')}
					</Button>
					<Button variant="link" onClick={entrarConContrasena}>
						{translate('movil.biometria.bloqueo.contrasena')}
					</Button>
				</div>
			)}
		</div>,
		document.body
	);
};

export default BloqueoBiometrico;
